import React, { useState } from "react";
import {
  safeJSONParse,
  sanitizeText,
  toTitleCase,
} from "../../../utils/helper";
import {
  calculateRequiredQualificationsMet,
  calculateResumeMiningAnswersMet,
} from "../helper";
import styles from "../agent-response.module.scss";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import rightTick from "../../../assets/images/greentick.png";
import crossTick from "../../../assets/images/redcross.png";
import clsx from "clsx";
import MarkdownRenderer from "../../MarkdownRenderer";

type ScoringAgentProps = {
  parsedContent: any;
  agentId: string | undefined;
};

// Container name to SAS token mapping
const CONTAINER_SAS_TOKEN_MAP: Record<string, string> = {
  "g42-resume-ui":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9 || "",
  "g42-resume-embedded-v9":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9 || "",
  "g42-resume-embedded-v9-1":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_1 || "",
  "g42-resume-embedded-v9-2":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_2 || "",
  "g42-resume-embedded-v9-3":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_3 || "",
  "g42-resume-embedded-v9-4":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_4 || "",
  "g42-resume-embedded-v9-5":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_5 || "",
  "g42-resume-embedded-v9-6":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_6 || "",
  "ailabs-raw-resume":
    import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9_ || "",
};

// Extracts the container name from a blob URL
const extractContainerName = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    // Path format: /container-name/file-name
    const pathParts = urlObj.pathname.split("/").filter(Boolean);
    return pathParts.length > 0 ? pathParts[0] : null;
  } catch {
    return null;
  }
};

// Gets the appropriate SAS token for a given blob URL
const getSasTokenForUrl = (blobUrl: string): string => {
  const containerName = extractContainerName(blobUrl);
  if (!containerName) {
    console.warn("Could not extract container name from URL:", blobUrl);
    return (
      import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9 || ""
    );
  }

  const sasToken = CONTAINER_SAS_TOKEN_MAP[containerName];
  if (!sasToken) {
    console.warn(`No SAS token found for container: ${containerName}`);
    return (
      import.meta.env.VITE_TEMPLATE_RESUME_BLOB_SAS_TOKEN_EMBEDDED_V9 || ""
    );
  }

  return sasToken;
};

export default function ScoringAgent({
  parsedContent,
  agentId,
}: ScoringAgentProps): JSX.Element {
  const [qualificationsOpen, setQualificationsOpen] = useState(false);
  const [skillsAndPreferencesOpen, setSkillsAndPreferencesOpen] =
    useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [prefQualificationsOpen, setPrefQualificationsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [miningOpen, setMiningOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const answer = safeJSONParse(parsedContent?.answer);
  const question = parsedContent?.question;
  console.log("HR AGENT ANSWER", answer);

  // Check if there are no candidates
  const candidates = answer?.top_candidates || [];
  if (answer == null) {
    const parsedCon = safeJSONParse(parsedContent);
    const answerMarkdown = parsedCon?.answer;
    return (
      <div>
        <MarkdownRenderer content={answerMarkdown?.toString()} />
        <div className={styles.finalQuestion}>{question}</div>
      </div>
    );
  }
  if (candidates.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
          No candidates found
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        color: "#333",
        width: "100%",
        overflowX: "auto",
      }}
    >
      <div className="responseTable">
        <h4
          style={{
            color: "#004141",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {candidates.length === 1
            ? "Below is the top 1 candidate based on:"
            : `Below are the top ${candidates.length} candidates based on:`}
        </h4>
        <ol style={{ marginTop: 0 }}>
          <li>Alignment with functional and technical requirements</li>
          <li>Strength of preferred qualification scores</li>
          {/* <li>Historical interview feedback, if any</li> */}
          <li>Current role, company, and location</li>
          <li>Evaluation based on first round interview fit</li>
        </ol>
      </div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: "20px",
          marginBottom: "40px",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "1px solid #ddd",
                padding: 8,
                width: "30%",
                minWidth: answer.top_candidates.length >= 3 ? "350px" : "auto",
                color: "#004141",
                fontSize: 12,
              }}
            >
              Category
            </th>
            {(answer.top_candidates || []).map((c: any, idx: number) => {
              const candidateCount = answer.top_candidates.length;
              const candidateColumnWidth =
                candidateCount > 0 ? `${70 / candidateCount}%` : "auto";
              return (
                <th
                  key={idx}
                  style={{
                    border: "1px solid #ddd",
                    padding: "6px",
                    color: "#004141",
                    fontSize: 12,
                    width: candidateColumnWidth,
                  }}
                >
                  <div style={{ fontWeight: 500 }}>
                    {toTitleCase(sanitizeText(c.name))}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {/* ========== REQUIRED QUALIFICATIONS SECTION ========== */}
          <tr
            style={{
              cursor: "pointer",
            }}
            onClick={() => setQualificationsOpen(!qualificationsOpen)}
          >
            <td
              style={{
                border: "1px solid #ddd",
                borderRight: "none",
                padding: "4px",
                fontWeight: 500,
                fontSize: 11,
                minWidth: answer.top_candidates.length >= 3 ? "350px" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ flexShrink: 0 }}>
                  {qualificationsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
                <span style={{ lineHeight: "1.3" }}>
                  Required Qualifications
                </span>
              </div>
            </td>
            {answer.top_candidates.map((c: any, idx: number) => {
              const { met, total } = calculateRequiredQualificationsMet(c);
              const percentage =
                total > 0 ? Math.round((met / total) * 100) : 0;
              const isLast = idx === answer.top_candidates.length - 1;

              // Determine label and style based on percentage
              let label = "";
              let scoreClass = "";
              let customStyle = {};

              if (percentage === 100) {
                label = "Excellent";
                scoreClass = styles.high;
              } else if (percentage > 0 && percentage < 100) {
                label = "Moderate";
                scoreClass = styles.low;
              } else if (percentage === 0) {
                label = "Weak";
                customStyle = { backgroundColor: "#dc2626", color: "white" };
              }

              return (
                <td
                  key={idx}
                  style={{
                    borderTop: "1px solid #ddd",
                    borderBottom: "1px solid #ddd",
                    borderLeft: "none",
                    borderRight: isLast ? "1px solid #ddd" : "none",
                    padding: "4px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <div>
                    {label && (
                      <span
                        className={clsx(styles.score, scoreClass)}
                        style={customStyle}
                      >
                        {label}
                      </span>
                    )}
                  </div>
                </td>
              );
            })}
          </tr>

          {qualificationsOpen &&
            ["education", "location", "experience"].map((field, fIdx) => {
              const baseValue =
                answer.top_candidates[0]?.skills_match
                  ?.required_qualification?.[field];

              const displayValue =
                field === "experience" && baseValue
                  ? `${baseValue}`
                  : baseValue || "-";

              return (
                <tr key={fIdx}>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px",
                      paddingLeft: "20px",
                      fontSize: 11,
                      verticalAlign: "middle",
                    }}
                  >
                    {sanitizeText(
                      field.charAt(0).toUpperCase() + field.slice(1),
                    )}
                    : {sanitizeText(displayValue)}
                  </td>

                  {answer.top_candidates.map((c: any, cIdx: number) => {
                    const rq = c.skills_match?.required_qualification || {};

                    const qualifiedKey = `is_${field}_qualified`;
                    const isYes = rq[qualifiedKey]?.toLowerCase() === "yes";

                    return (
                      <td
                        key={cIdx}
                        style={{
                          border: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "center",
                          verticalAlign: "top",
                          fontSize: 11,
                        }}
                      >
                        {/* ✔️ / ❌ */}
                        <div style={{ marginBottom: "4px" }}>
                          <img
                            src={isYes ? rightTick : crossTick}
                            alt="status"
                            style={{
                              width: "20px",
                              height: "auto",
                            }}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}

          {/* ========== SKILLS AND PREFERENCES PARENT SECTION ========== */}
          <tr
            style={{
              cursor: "pointer",
            }}
            onClick={() =>
              setSkillsAndPreferencesOpen(!skillsAndPreferencesOpen)
            }
          >
            <td
              style={{
                border: "1px solid #ddd",
                borderRight: "none",
                padding: "4px",
                fontWeight: 500,
                fontSize: 11,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ flexShrink: 0 }}>
                  {skillsAndPreferencesOpen ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </span>
                <span style={{ lineHeight: "1.3" }}>
                  Skills and Preferred Qualifications Fit
                </span>
              </div>
            </td>
            {answer.top_candidates.map((c: any, idx: number) => {
              const resumeScore = c.resume_score || 0;
              const isLast = idx === answer.top_candidates.length - 1;

              // Skills Match: >=80% = Excellent, 60-80% = Moderate, <60% = Low
              let scoreClass;
              let matchText;
              if (resumeScore >= 80) {
                scoreClass = styles.high;
                matchText = "Excellent: >80% Match";
              } else if (resumeScore >= 60) {
                scoreClass = styles.low;
                matchText = "Moderate: 60-80% Match";
              } else {
                scoreClass = styles.medium;
                matchText = "Low: <60% Match";
              }

              return (
                <td
                  key={idx}
                  style={{
                    borderTop: "1px solid #ddd",
                    borderBottom: "1px solid #ddd",
                    borderLeft: "none",
                    borderRight: isLast ? "1px solid #ddd" : "none",
                    padding: "4px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <div>
                    <span className={clsx(styles.score, scoreClass)}>
                      {matchText}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>

          {skillsAndPreferencesOpen && (
            <>
              {/* ========== FUNCTIONAL SKILLS SECTION ========== */}
              <tr
                style={{
                  cursor: "pointer",
                }}
                onClick={() => setSkillsOpen(!skillsOpen)}
              >
                <td
                  colSpan={answer.top_candidates.length + 1}
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    paddingLeft: "30px",
                    fontWeight: 500,
                    fontSize: 11,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>
                      {skillsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </span>
                    <span style={{ lineHeight: "1.3" }}>
                      Functional/Technical Skills
                    </span>
                  </div>
                </td>
              </tr>

              {skillsOpen &&
                (
                  answer.top_candidates[0]?.skills_match?.functional_skills ||
                  []
                ).map((skill: any, sIdx: number) => (
                  <tr key={sIdx}>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "4px",
                        paddingLeft: "50px",
                        fontSize: 11,
                        verticalAlign: "middle",
                      }}
                    >
                      {sanitizeText(skill.skill)}
                    </td>

                    {answer.top_candidates.map((c: any, cIdx: number) => {
                      const match = c.skills_match?.functional_skills?.find(
                        (x: any) => x.skill === skill.skill,
                      );
                      return (
                        <td
                          key={cIdx}
                          style={{
                            border: "1px solid #ddd",
                            padding: "4px",
                            textAlign: "center",
                            fontSize: 11,
                          }}
                        >
                          {match?.result === "Yes" ? (
                            <img
                              src={rightTick}
                              alt="Preview"
                              style={{
                                width: "20px",
                                height: "auto",
                              }}
                            />
                          ) : (
                            <img
                              src={crossTick}
                              alt="Preview"
                              style={{
                                width: "20px",
                                height: "auto",
                              }}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}

              {/* ========== PREFERRED QUALIFICATIONS SECTION ========== */}
              <tr
                style={{
                  cursor: "pointer",
                }}
                onClick={() =>
                  setPrefQualificationsOpen(!prefQualificationsOpen)
                }
              >
                <td
                  colSpan={answer.top_candidates.length + 1}
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    paddingLeft: "30px",
                    fontWeight: 500,
                    fontSize: 11,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>
                      {prefQualificationsOpen ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </span>
                    <span style={{ lineHeight: "1.3" }}>
                      Preferred Qualifications
                    </span>
                  </div>
                </td>
              </tr>

              {prefQualificationsOpen &&
                answer.top_candidates[0]?.skills_match?.preferred_qualification?.map(
                  (pref: any, fIdx: number) => {
                    return (
                      <tr key={fIdx}>
                        {/* ---------- LABEL COLUMN ---------- */}
                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: "4px",
                            paddingLeft: "50px",
                            fontSize: 11,
                            verticalAlign: "middle",
                          }}
                        >
                          {sanitizeText(pref.entity_type)}:{" "}
                          {sanitizeText(pref.entity_value) || "-"}
                        </td>

                        {/* ---------- CANDIDATE COLUMNS ---------- */}
                        {answer.top_candidates.map((c: any, cIdx: number) => {
                          const matches =
                            c.skills_match?.preferred_qualification || [];

                          const match = matches.find(
                            (m: any) =>
                              m.entity_type === pref.entity_type &&
                              m.entity_value === pref.entity_value,
                          );

                          const isYes = match?.result?.toLowerCase() === "yes";
                          const reasoning = match?.resoning;

                          return (
                            <td
                              key={cIdx}
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                                textAlign: "center",
                                fontSize: 11,
                                verticalAlign: "top",
                              }}
                            >
                              {/* ✔️ / ❌ */}
                              <div style={{ marginBottom: 4 }}>
                                <img
                                  src={isYes ? rightTick : crossTick}
                                  alt={isYes ? "Qualified" : "Not qualified"}
                                  style={{ width: 20 }}
                                />
                              </div>

                              {/* Reasoning (CENTERED BELOW) */}
                              {reasoning && (
                                <div
                                  style={{
                                    color: "#555",
                                    marginTop: "2px",
                                    lineHeight: "1.4",
                                    textAlign: "center",
                                    fontSize: 11,
                                  }}
                                >
                                  {sanitizeText(reasoning)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  },
                )}
            </>
          )}

          {/* ========== INTERVIEW FEEDBACK SECTION ========== */}
          {agentId !== "scoring_agent_new" && (
            <>
              <tr
                style={{ cursor: "pointer" }}
                onClick={() => setFeedbackOpen(!feedbackOpen)}
              >
                <td
                  style={{
                    border: "1px solid #ddd",
                    borderRight: "none",
                    padding: "4px",
                    fontWeight: 500,
                    fontSize: 11,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>
                      {feedbackOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </span>
                    <span style={{ lineHeight: "1.3" }}>
                      Past Interview Feedback
                    </span>
                  </div>
                </td>
                {answer.top_candidates.map((c: any, idx: number) => {
                  const isLast = idx === answer.top_candidates.length - 1;

                  let hiringRec = c.hiring_recommendation?.toLowerCase() || "";
                  let scoreClass;
                  if (hiringRec === "hire") {
                    hiringRec = c.hiring_recommendation;
                    scoreClass = styles.high;
                  } else if (hiringRec === "maybe") {
                    hiringRec = c.hiring_recommendation;
                    scoreClass = styles.low;
                  } else if (hiringRec) {
                    hiringRec = "Not available";
                    scoreClass = styles.medium;
                  }

                  return (
                    <td
                      key={idx}
                      style={{
                        borderTop: "1px solid #ddd",
                        borderBottom: "1px solid #ddd",
                        borderLeft: "none",
                        borderRight: isLast ? "1px solid #ddd" : "none",
                        padding: "4px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <div>
                        {c.hiring_recommendation && (
                          <span className={clsx(styles.score, scoreClass)}>
                            {hiringRec}
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>

              {feedbackOpen &&
                ["recommendation"].map((field, fIdx) => (
                  <tr key={fIdx}>
                    {/* Left label */}
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "4px",
                        paddingLeft: "20px",
                        fontSize: 11,
                        verticalAlign: "middle",
                      }}
                    >
                      {/* {sanitizeText(field.charAt(0).toUpperCase() + field.slice(1))} */}
                      Summary
                    </td>

                    {/* Candidate columns */}
                    {answer.top_candidates.map((c: any, cIdx: number) => {
                      const value = c.interview_feedback; // ✅ feedback is now a STRING

                      return (
                        <td
                          key={cIdx}
                          style={{
                            border: "1px solid #ddd",
                            padding: "4px",
                            verticalAlign: "top",
                            fontSize: 11,
                            textAlign: "center",
                          }}
                        >
                          {value && value.trim() ? sanitizeText(value) : "-"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </>
          )}

          {/* ========== RESUME MINING ANSWERS SECTION ========== */}
          <tr
            style={{
              cursor: "pointer",
            }}
            onClick={() => setMiningOpen(!miningOpen)}
          >
            <td
              style={{
                border: "1px solid #ddd",
                borderRight: "none",
                padding: "4px",
                fontWeight: 500,
                fontSize: 11,
                minWidth: answer.top_candidates.length >= 3 ? "350px" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ flexShrink: 0 }}>
                  {miningOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
                <span style={{ lineHeight: "1.3" }}>
                  First-interview screening questions
                </span>
              </div>
            </td>
            {answer.top_candidates.map((c: any, idx: number) => {
              const { met, total } = calculateResumeMiningAnswersMet(c);
              const percentage =
                total > 0 ? Math.round((met / total) * 100) : 0;
              const isLast = idx === answer.top_candidates.length - 1;

              // Experience Evaluation: >=80% = high, 60-80% = low, <60% = show nothing
              let scoreClass;
              let matchText;
              if (percentage >= 80) {
                scoreClass = styles.high;
                matchText = "Excellent";
              } else if (percentage >= 60) {
                scoreClass = styles.low;
                matchText = "Moderate";
              } else {
                scoreClass = styles.medium;
                matchText = "Low";
              }

              return (
                <td
                  key={idx}
                  style={{
                    borderTop: "1px solid #ddd",
                    borderBottom: "1px solid #ddd",
                    borderLeft: "none",
                    borderRight: isLast ? "1px solid #ddd" : "none",
                    padding: "4px",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <div>
                    <span className={clsx(styles.score, scoreClass)}>
                      {matchText}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>

          {miningOpen &&
            (answer.top_candidates[0]?.resume_mining_answers || []).map(
              (qa: any, qIdx: number) => (
                <tr key={qIdx}>
                  {/* Question Column */}
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px",
                      paddingLeft: "20px",
                      fontSize: 11,
                      verticalAlign: "middle",
                    }}
                  >
                    {sanitizeText(qa.question)}
                  </td>

                  {/* Candidate Columns */}
                  {answer.top_candidates.map((c: any, cIdx: number) => {
                    const item = c.resume_mining_answers?.find(
                      (x: any) => x.question === qa.question,
                    );

                    const yesNo = item?.answer?.toLowerCase();
                    const isYes = yesNo === "yes";

                    return (
                      <td
                        key={cIdx}
                        style={{
                          border: "1px solid #ddd",
                          padding: "4px",
                          textAlign: "center",
                          fontSize: 11,
                          verticalAlign: "top",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "22px",
                            marginBottom: "4px",
                          }}
                        >
                          {isYes ? (
                            <img
                              src={rightTick}
                              alt="Preview"
                              style={{
                                width: "20px",
                                height: "auto",
                              }}
                            />
                          ) : (
                            <img
                              src={crossTick}
                              alt="Preview"
                              style={{
                                width: "20px",
                                height: "auto",
                              }}
                            />
                          )}
                        </div>

                        <div
                          style={{
                            color: "#555",
                            marginTop: "2px",
                            lineHeight: "1.4",
                            textAlign: "center",
                            fontSize: 11,
                          }}
                        >
                          {sanitizeText(item?.reasoning)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ),
            )}

          {/* ========== SUMMARY SECTION ========== */}
          <tr
            style={{
              cursor: "pointer",
            }}
            onClick={() => setSummaryOpen(!summaryOpen)}
          >
            <td
              style={{
                border: "1px solid #ddd",
                borderRight: "none",
                padding: "4px",
                fontWeight: 500,
                fontSize: "11px",
                minWidth: answer.top_candidates.length >= 3 ? "350px" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ flexShrink: 0 }}>
                  {summaryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
                <span style={{ lineHeight: "1.3" }}>
                  Employment (Last known)
                </span>
              </div>
            </td>
            {answer.top_candidates.map((c: any, idx: number) => {
              const isLast = idx === answer.top_candidates.length - 1;
              return (
                <td
                  key={idx}
                  style={{
                    borderTop: "1px solid #ddd",
                    borderBottom: "1px solid #ddd",
                    borderLeft: "none",
                    borderRight: isLast ? "1px solid #ddd" : "none",
                    padding: "4px",
                    textAlign: "center",
                    verticalAlign: "middle",
                    fontSize: 11,
                  }}
                >
                  <div
                    style={{
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {toTitleCase(sanitizeText(c.current_title))}
                  </div>
                </td>
              );
            })}
          </tr>

          {summaryOpen && (
            <>
              <tr>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    paddingLeft: "20px",
                    fontSize: 11,
                    verticalAlign: "middle",
                  }}
                >
                  Company
                </td>
                {answer.top_candidates.map((c: any, idx: number) => (
                  <td
                    key={idx}
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px",
                      fontSize: 11,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "200px",
                      textAlign: "center",
                    }}
                  >
                    {toTitleCase(sanitizeText(c.current_company))}
                  </td>
                ))}
              </tr>

              {/* LinkedIn */}
              <tr>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    fontSize: 11,
                    paddingLeft: "20px",
                    verticalAlign: "middle",
                  }}
                >
                  LinkedIn
                </td>
                {answer.top_candidates.map((c: any, idx: number) => {
                  let url = c.linkedin_url?.trim() || "";

                  // Check if text looks like a LinkedIn URL (handles missing http://)
                  const isLikelyLinkedIn =
                    typeof url === "string" &&
                    (url.includes("linkedin.com") || url.includes("lnkd.in"));

                  // If it's a LinkedIn URL but missing protocol, add https://
                  if (isLikelyLinkedIn && !url.startsWith("http")) {
                    url = "https://" + url;
                  }

                  return (
                    <td
                      key={idx}
                      style={{
                        border: "1px solid #ddd",
                        padding: "4px",
                        fontSize: 11,
                        textAlign: "center",
                      }}
                    >
                      {isLikelyLinkedIn ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#0077b5",
                            textDecoration: "underline",
                            fontSize: 11,
                          }}
                        >
                          View Profile
                        </a>
                      ) : (
                        c.linkedin_url || "-"
                      )}
                    </td>
                  );
                })}
              </tr>

              {/* Location */}
              <tr>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    fontSize: 11,
                    paddingLeft: "20px",
                    verticalAlign: "middle",
                  }}
                >
                  Location
                </td>
                {answer.top_candidates.map((c: any, idx: number) => (
                  <td
                    key={idx}
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px",
                      fontSize: 11,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "200px",
                      textAlign: "center",
                    }}
                  >
                    {toTitleCase(sanitizeText(c.current_location))}
                  </td>
                ))}
              </tr>
            </>
          )}

          {/* ========== DOWNLOAD SECTION ========== */}
          {agentId !== "scoring_agent_new" && (
            <tr>
              <td
                style={{
                  border: "1px solid #ddd",
                  padding: "4px",
                  paddingLeft: "20px",
                  fontSize: 11,
                  fontWeight: 500,
                  verticalAlign: "middle",
                }}
              >
                Resume
              </td>
              {answer.top_candidates.map((c: any, idx: number) => {
                const sasToken = c.path ? getSasTokenForUrl(c.path) : "";
                const resumeUrl =
                  c.path && sasToken ? `${c.path}?${sasToken}` : c.path;

                return (
                  <td
                    key={idx}
                    style={{
                      border: "1px solid #ddd",
                      padding: "4px",
                      fontSize: 11,
                      textAlign: "center",
                    }}
                  >
                    {resumeUrl ? (
                      <a
                        href={resumeUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#0077b5",
                          textDecoration: "underline",
                          fontSize: 11,
                          cursor: "pointer",
                        }}
                      >
                        View Resume
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>

      <div className={styles.finalQuestion}>{question}</div>
    </div>
  );
}
